"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";

export function SiteChrome() {
  const pathname = usePathname();

  useEffect(() => {
    const header = document.querySelector(".site-header");
    if (!header) return;
    const onScroll = () => {
      header.classList.toggle("is-scrolled", window.scrollY > 8);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      if ("scrollRestoration" in window.history) {
        window.history.scrollRestoration = "manual";
      }
      window.scrollTo(0, 0);
    }
  }, [pathname]);

  // --- Final fix: Zoho widget hidden after language/route change ---
  // Root cause: Zoho SalesIQ calls floatbutton.visible("hide") internally when it
  // detects a SPA navigation (URL change). This sets Zoho's INTERNAL state — it
  // cannot be overridden by CSS or DOM style manipulation alone.
  // Fix part 1 (mount): persistent style observer as a fallback for inline-opacity animations.
  // Fix part 2 (pathname): call Zoho's own API to re-show the button after each navigation.

  useEffect(() => {
    if (typeof window === "undefined") return;

    const ZOHO_SELECTORS = ["#zsiq_float", ".zsiq_floatmain"];
    const observed = new WeakSet<HTMLElement>();
    const styleObservers: MutationObserver[] = [];

    const updateOpenState = () => {
      const iframe = document.querySelector<HTMLElement>("#siqiframe");
      const floatBtn = document.querySelector<HTMLElement>("#zsiq_float") || document.querySelector<HTMLElement>(".zsiq_floatmain");
      if (!iframe) return;

      const style = window.getComputedStyle(iframe);
      const isVisible = style.display !== "none" && 
                        style.visibility !== "hidden" && 
                        parseFloat(style.opacity || "1") > 0.1 && 
                        iframe.getBoundingClientRect().height > 40;

      if (isVisible) {
        document.body.classList.add("zoho-open");
        if (floatBtn) floatBtn.classList.add("is-open");
      } else {
        document.body.classList.remove("zoho-open");
        if (floatBtn) floatBtn.classList.remove("is-open");
      }
    };

    const forceVisible = (el: HTMLElement) => {
      if (el.style.opacity !== "1") el.style.opacity = "1";
      if (el.style.display === "none") el.style.display = "";
      if (el.style.visibility && el.style.visibility !== "visible") el.style.visibility = "visible";
      updateOpenState();
    };

    const attachStyleObserver = (el: HTMLElement) => {
      if (observed.has(el)) return;
      observed.add(el);
      forceVisible(el);
      const mo = new MutationObserver(() => {
        forceVisible(el);
        updateOpenState();
      });
      mo.observe(el, { attributes: true, attributeFilter: ["style", "class"] });
      styleObservers.push(mo);

      el.addEventListener("click", () => {
        const wasOpen = document.body.classList.contains("zoho-open") || el.classList.contains("is-open");
        if (wasOpen) {
          document.body.classList.remove("zoho-open");
          el.classList.remove("is-open");
        } else {
          document.body.classList.add("zoho-open");
          el.classList.add("is-open");
        }
        setTimeout(updateOpenState, 150);
        setTimeout(updateOpenState, 500);
      });
    };

    ZOHO_SELECTORS.forEach((sel) => {
      const el = document.querySelector<HTMLElement>(sel);
      if (el) attachStyleObserver(el);
    });

    const domObserver = new MutationObserver(() => {
      ZOHO_SELECTORS.forEach((sel) => {
        const el = document.querySelector<HTMLElement>(sel);
        if (el) attachStyleObserver(el);
      });

      const iframe = document.querySelector<HTMLElement>("#siqiframe");
      if (iframe && !observed.has(iframe)) {
        observed.add(iframe);
        const mo = new MutationObserver(updateOpenState);
        mo.observe(iframe, { attributes: true, attributeFilter: ["style", "class", "height"] });
        styleObservers.push(mo);
        updateOpenState();
      }
    });
    domObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      domObserver.disconnect();
      styleObservers.forEach((mo) => mo.disconnect());
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // mount-once — style observer persists forever

  // Fix part 2: after every navigation, call Zoho's API to restore the float button.
  // This is the CRITICAL fix — Zoho hides itself via internal state on SPA nav.
  useEffect(() => {
    if (typeof window === "undefined") return;

    const showZoho = () => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const z = (window as any).$zoho;
        if (z?.salesiq?.floatbutton?.visible) {
          z.salesiq.floatbutton.visible("show");
        }
        // Also force DOM visibility as belt-and-suspenders
        const el = document.querySelector<HTMLElement>("#zsiq_float");
        if (el) {
          el.style.opacity = "1";
          el.style.display = "";
          el.style.visibility = "visible";
        }
      } catch {
        // Zoho not loaded yet — safe to ignore
      }
    };

    // Run immediately
    showZoho();
    // Zoho may process the navigation event asynchronously — run again after delays
    const t1 = setTimeout(showZoho, 200);
    const t2 = setTimeout(showZoho, 800);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [pathname]); // fires on every route/language change

  useEffect(() => {
    const toggle = document.querySelector<HTMLButtonElement>(".menu-toggle");
    const menu = document.querySelector<HTMLElement>(".mobile-menu");
    if (!toggle || !menu) return;

    const onToggle = () => {
      const open = menu.classList.toggle("is-open");
      toggle.setAttribute("aria-expanded", open ? "true" : "false");
      document.body.style.overflow = open ? "hidden" : "";
    };
    toggle.addEventListener("click", onToggle);

    const closers = menu.querySelectorAll("a");
    const onClose = () => {
      menu.classList.remove("is-open");
      toggle.setAttribute("aria-expanded", "false");
      document.body.style.overflow = "";
    };
    closers.forEach((a) => a.addEventListener("click", onClose));

    return () => {
      toggle.removeEventListener("click", onToggle);
      closers.forEach((a) => a.removeEventListener("click", onClose));
    };
  }, [pathname]);

  useEffect(() => {
    const els = document.querySelectorAll<HTMLElement>(".reveal");
    if (!("IntersectionObserver" in window) || els.length === 0) {
      els.forEach((el) => el.classList.add("is-in"));
      return;
    }
    els.forEach((el) => el.classList.remove("is-in"));
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.05, rootMargin: "0px" }
    );
    els.forEach((el) => {
      if (!el.style.getPropertyValue("--reveal-delay")) {
        const group = el.closest("[data-reveal-group]");
        if (group) {
          const idx = Array.from(group.querySelectorAll(".reveal")).indexOf(el);
          el.style.setProperty("--reveal-delay", `${idx * 60}ms`);
        }
      }
      io.observe(el);
    });
    return () => io.disconnect();
  }, [pathname]);

  useEffect(() => {
    const counters = document.querySelectorAll<HTMLElement>("[data-count]");
    if (counters.length === 0) return;

    const formatNum = (n: number, decimals: number) => {
      if (decimals > 0) return n.toFixed(decimals);
      return Math.round(n).toLocaleString();
    };
    const animate = (el: HTMLElement) => {
      const target = parseFloat(el.dataset.count ?? "0");
      const decimals = parseInt(el.dataset.decimals ?? "0", 10);
      const dur = parseInt(el.dataset.duration ?? "1400", 10);
      const start = performance.now();
      const tick = (now: number) => {
        const t = Math.min(1, (now - start) / dur);
        const eased = 1 - Math.pow(1 - t, 3);
        el.textContent = formatNum(target * eased, decimals);
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };
    const cio = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            animate(e.target as HTMLElement);
            cio.unobserve(e.target);
          }
        });
      },
      { threshold: 0.4 }
    );
    counters.forEach((el) => cio.observe(el));
    return () => cio.disconnect();
  }, [pathname]);

  return null;
}
